import { useEffect, useMemo, useState } from "react"
import { Alert, Button, Col, Container, FloatingLabel, Form, Row, Spinner } from "react-bootstrap"
import { apiClient } from "../services/api/client"

const DEFAULT_SETTINGS = {
  workStart: "06:00",
  workEnd: "16:00",
  closedHolidays: true,
  closedSaturday: true,
  closedSunday: true,
  slotIntervalMinutes: 30,
  breakSlots: [],
  onlineStands: [],
  maxStani: 0,
}

const INTERVAL_OPTIONS = [15, 30, 60]

const toMinutes = (value) => {
  const match = String(value || "").match(/^(\d{2}):(\d{2})$/)
  if (!match) return null
  const h = Number(match[1])
  const m = Number(match[2])
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  if (h < 0 || h > 23 || m < 0 || m > 59) return null
  return h * 60 + m
}

const toClock = (minutes) => {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const buildBreakSlotOptions = (workStart, workEnd, interval) => {
  const start = toMinutes(workStart)
  const end = toMinutes(workEnd)
  const step = Number(interval)
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(step) || step <= 0 || end <= start) {
    return []
  }

  const options = []
  for (let current = start; current + step <= end; current += step) {
    const slotStart = toClock(current)
    const slotEnd = toClock(current + step)
    options.push({ value: slotStart, label: `${slotStart} - ${slotEnd}` })
  }
  return options
}

const CalendarSettings = ({ tabActive }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const standOptions = useMemo(() => {
    const max = Number(settings.maxStani || 0)
    return max > 0 ? Array.from({ length: max }, (_, i) => i + 1) : []
  }, [settings.maxStani])

  const breakSlotOptions = useMemo(() => {
    return buildBreakSlotOptions(settings.workStart, settings.workEnd, settings.slotIntervalMinutes)
  }, [settings.workStart, settings.workEnd, settings.slotIntervalMinutes])

  const fetchSettings = async () => {
    setLoading(true)
    setError("")
    setSuccess("")
    try {
      const data = await apiClient.get("/rezervace/calendar/settings")
      setSettings({
        ...DEFAULT_SETTINGS,
        ...data,
        breakSlots: Array.isArray(data?.breakSlots) ? data.breakSlots : [],
        onlineStands: Array.isArray(data?.onlineStands) ? data.onlineStands : [],
      })
    } catch (requestError) {
      setError(requestError.message || "Nepodařilo se načíst nastavení kalendáře.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!tabActive) return
    fetchSettings()
  }, [tabActive])

  useEffect(() => {
    if (!breakSlotOptions.length) {
      if (settings.breakSlots.length > 0) {
        setSettings((prev) => ({ ...prev, breakSlots: [] }))
      }
      return
    }
    const allowed = new Set(breakSlotOptions.map((slot) => slot.value))
    const filtered = settings.breakSlots.filter((slot) => allowed.has(slot))
    if (filtered.length !== settings.breakSlots.length) {
      setSettings((prev) => ({ ...prev, breakSlots: filtered }))
    }
  }, [breakSlotOptions, settings.breakSlots])

  const onChange = (event) => {
    const { name, type, checked, value } = event.target
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const toggleBreakSlot = (slotValue) => {
    setSettings((prev) => {
      const current = new Set(prev.breakSlots)
      if (current.has(slotValue)) {
        current.delete(slotValue)
      } else {
        current.add(slotValue)
      }
      return {
        ...prev,
        breakSlots: Array.from(current).sort(),
      }
    })
  }

  const toggleOnlineStand = (stand) => {
    setSettings((prev) => {
      const current = new Set((prev.onlineStands || []).map(Number))
      if (current.has(stand)) {
        current.delete(stand)
      } else {
        current.add(stand)
      }
      return {
        ...prev,
        onlineStands: Array.from(current).sort((a, b) => a - b),
      }
    })
  }

  const handleSave = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError("")
    setSuccess("")
    try {
      const payload = {
        workStart: settings.workStart,
        workEnd: settings.workEnd,
        closedHolidays: settings.closedHolidays,
        closedSaturday: settings.closedSaturday,
        closedSunday: settings.closedSunday,
        slotIntervalMinutes: Number(settings.slotIntervalMinutes),
        breakSlots: settings.breakSlots,
        onlineStands: settings.onlineStands,
      }
      const response = await apiClient.put("/rezervace/calendar/settings", payload)
      const serverSettings = response?.settings || {}
      setSettings((prev) => ({
        ...prev,
        ...serverSettings,
        breakSlots: Array.isArray(serverSettings?.breakSlots) ? serverSettings.breakSlots : [],
        onlineStands: Array.isArray(serverSettings?.onlineStands) ? serverSettings.onlineStands : [],
      }))
      setSuccess(response?.message || "Nastavení kalendáře bylo uloženo.")
    } catch (requestError) {
      setError(requestError.message || "Nepodařilo se uložit nastavení kalendáře.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container fluid className="px-0">
      <Row>
        <Col lg={6}>
          <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
            <div>
              <h5 className="mb-1">Nastavení kalendáře</h5>
              <p className="text-muted small mb-0">Společné nastavení pro interní i veřejné rezervace.</p>
            </div>
            <Button variant="outline-secondary" onClick={fetchSettings} disabled={loading || saving}>
              Obnovit
            </Button>
          </div>

          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted small py-2">
              <Spinner size="sm" />
              Načítám nastavení...
            </div>
          ) : (
            <Form onSubmit={handleSave}>
              <Row className="g-2">
                <Col md={6}>
                  <FloatingLabel label="Provoz od">
                    <Form.Control
                      type="time"
                      name="workStart"
                      value={settings.workStart}
                      onChange={onChange}
                      required
                      step={300}
                    />
                  </FloatingLabel>
                </Col>
                <Col md={6}>
                  <FloatingLabel label="Provoz do">
                    <Form.Control
                      type="time"
                      name="workEnd"
                      value={settings.workEnd}
                      onChange={onChange}
                      required
                      step={300}
                    />
                  </FloatingLabel>
                </Col>

                <Col xs={12}>
                  <div className="border rounded-3 p-3 bg-body">
                    <div className="fw-semibold mb-2">Zavřené dny</div>
                    <Form.Check
                      id="closed-holidays"
                      type="checkbox"
                      label="Státní svátky"
                      name="closedHolidays"
                      checked={Boolean(settings.closedHolidays)}
                      onChange={onChange}
                    />
                    <Form.Check
                      id="closed-saturday"
                      type="checkbox"
                      label="Sobota"
                      name="closedSaturday"
                      checked={Boolean(settings.closedSaturday)}
                      onChange={onChange}
                    />
                    <Form.Check
                      id="closed-sunday"
                      type="checkbox"
                      label="Neděle"
                      name="closedSunday"
                      checked={Boolean(settings.closedSunday)}
                      onChange={onChange}
                    />
                  </div>
                </Col>

                <Col md={6}>
                  <FloatingLabel label="Výchozí interval">
                    <Form.Select
                      name="slotIntervalMinutes"
                      value={settings.slotIntervalMinutes}
                      onChange={onChange}
                    >
                      {INTERVAL_OPTIONS.map((value) => (
                        <option key={value} value={value}>
                          {value} minut
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Col>

                <Col xs={12}>
                  <div className="border rounded-3 p-3 bg-body">
                    <div className="fw-semibold mb-2">Přestávka</div>
                    <div className="small text-muted mb-2">Vyber sloty, které se nemají nabízet v kalendáři.</div>
                    {breakSlotOptions.length === 0 ? (
                      <div className="small text-muted">Nejprve nastavte platnou provozní dobu.</div>
                    ) : (
                      <Row className="g-2">
                        {breakSlotOptions.map((slot) => (
                          <Col key={slot.value} xs={12} sm={6} md={4}>
                            <Form.Check
                              id={`break-${slot.value}`}
                              type="checkbox"
                              label={slot.label}
                              checked={settings.breakSlots.includes(slot.value)}
                              onChange={() => toggleBreakSlot(slot.value)}
                            />
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                </Col>

                <Col xs={12}>
                  <div className="border rounded-3 p-3 bg-body">
                    <div className="fw-semibold mb-2">Online rezervace - povolená stání</div>
                    <div className="small text-muted mb-2">
                      Počet stání ve středisku: <strong>{settings.maxStani || 0}</strong>
                    </div>
                    {standOptions.length === 0 ? (
                      <div className="small text-muted">Nejprve nastavte počet stání ve středisku.</div>
                    ) : (
                      <Row className="g-2">
                        {standOptions.map((stand) => (
                          <Col key={`stand-${stand}`} xs={6} md={3}>
                            <Form.Check
                              id={`online-stand-${stand}`}
                              type="checkbox"
                              label={`Stání ${stand}`}
                              checked={settings.onlineStands.includes(stand)}
                              onChange={() => toggleOnlineStand(stand)}
                            />
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                </Col>
              </Row>
              {error && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" className="mt-3 mb-0">
                  {success}
                </Alert>
              )}
              <div className="d-flex justify-content-start mt-3">
                <Button type="submit" variant="primary" disabled={saving || loading}>
                  {saving ? "Ukládám..." : "Uložit"}
                </Button>
              </div>                        
            </Form>
          )}
        </Col>
      </Row>          
    </Container>
  )
}

export default CalendarSettings
